// Music Selector Web Component
import { escapeHtml } from '../utils/html-utils.js';

class MusicSelector extends HTMLElement {
  constructor() {
    super();
    this.musicUrl = '';
    this.urlMetadata = null;
    this.currentJobId = null;
    this.statusCheckInterval = null;
    this.isLoading = false;
    this.debounceTimer = null;
    this.postText = '';
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  render() {
    this.innerHTML = `
      <div class="music-selector">
        <div class="music-header">
          <post-type-selector selected-type="music" id="musicPostTypeSelector"></post-type-selector>
          <p>Paste a link to a song, album, or playlist</p>
        </div>
        
        <div class="music-input-group">
          <input 
            type="url" 
            id="musicUrlInput"
            class="music-url-input"
            placeholder="https://open.spotify.com/track/... or any music URL"
            value="${this.musicUrl}"
          />
          <button 
            type="button" 
            class="clear-url-btn" 
            id="clearUrlBtn"
            title="Clear URL"
            style="display: ${this.musicUrl ? 'block' : 'none'};"
          >
            ✕
          </button>
        </div>
        
        <div class="url-status" id="urlStatus" style="display: none;">
          <div class="loading-indicator" id="loadingIndicator" style="display: none;">
            <div class="spinner"></div>
            <span>Loading preview...</span>
          </div>
          <div class="generating-indicator" id="generatingIndicator" style="display: none;">
            <div class="spinner"></div>
            <span>Generating preview...</span>
            <button class="refresh-btn" id="refreshBtn" title="Refresh status">
              🔄
            </button>
          </div>
          <div class="error-message" id="errorMessage" style="display: none;"></div>
        </div>
        
        <div class="music-preview" id="musicPreview" style="display: none;">
          <!-- Preview will be populated here -->
        </div>
        
        <div class="music-post-text" style="display: ${this.urlMetadata ? 'block' : 'none'};">
          <textarea 
            id="musicPostText"
            placeholder="Add your thoughts about this music... (optional)"
            rows="3"
          >${this.postText}</textarea>
        </div>
        
        <div class="music-actions" style="display: ${this.urlMetadata ? 'flex' : 'none'};">
          <button class="clear-music-btn" id="clearMusicBtn">Clear</button>
          <button class="share-music-btn" id="shareMusicBtn">Share Music</button>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Post type selector
    const postTypeSelector = this.querySelector('#musicPostTypeSelector');
    if (postTypeSelector) {
      postTypeSelector.addEventListener('post-type-selected', (e) => {
        // Dispatch event to parent post-writer to switch types
        this.dispatchEvent(new CustomEvent('switch-post-type', {
          detail: { type: e.detail.type },
          bubbles: true
        }));
      });
    }

    const urlInput = this.querySelector('#musicUrlInput');
    const clearUrlBtn = this.querySelector('#clearUrlBtn');
    const clearMusicBtn = this.querySelector('#clearMusicBtn');
    const shareMusicBtn = this.querySelector('#shareMusicBtn');
    const refreshBtn = this.querySelector('#refreshBtn');
    const postTextArea = this.querySelector('#musicPostText');

    // Post text input
    if (postTextArea) {
      postTextArea.addEventListener('input', (e) => {
        this.postText = e.target.value;
      });
    }

    // URL input with debounced validation
    urlInput.addEventListener('input', (e) => {
      this.musicUrl = e.target.value.trim();
      this.updateClearButtonVisibility();

      // Clear previous timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Debounce URL processing
      this.debounceTimer = setTimeout(() => {
        this.processUrl();
      }, 500);
    });

    // Clear URL button
    clearUrlBtn.addEventListener('click', () => {
      this.clearUrl();
    });

    // Clear music button
    clearMusicBtn.addEventListener('click', () => {
      this.clearMusic();
    });

    // Share music button
    shareMusicBtn.addEventListener('click', () => {
      this.shareMusic();
    });

    // Refresh button
    refreshBtn.addEventListener('click', () => {
      this.checkJobStatus();
    });
  }

  updateClearButtonVisibility() {
    const clearUrlBtn = this.querySelector('#clearUrlBtn');
    if (clearUrlBtn) {
      clearUrlBtn.style.display = this.musicUrl ? 'block' : 'none';
    }
  }

  async processUrl() {
    if (!this.musicUrl) {
      this.hideStatus();
      this.hidePreview();
      this.clearJobStatus();
      return;
    }

    // Basic URL validation
    if (!this.isValidUrl(this.musicUrl)) {
      this.showError('Please enter a valid URL');
      this.hidePreview();
      this.clearJobStatus();
      return;
    }

    this.showGenerating();
    this.hidePreview();

    try {
      // Queue metadata generation job
      const response = await fetch('/api/url-metadata', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: this.musicUrl,
          type: 'music'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.jobId) {
        this.currentJobId = result.jobId;
        this.startStatusPolling();
      } else {
        throw new Error('Failed to queue metadata generation');
      }

    } catch (error) {
      window.logger.error('Error queuing URL metadata generation:', error);
      this.showError('Could not start metadata generation');
      this.hidePreview();
      this.urlMetadata = null;
      this.clearJobStatus();
      this.updateActions();
    }
  }

  isValidUrl(string) {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (error) {
      window.logger.error('Error validating URL:', error);
      return false;
    }
  }

  showLoading() {
    const urlStatus = this.querySelector('#urlStatus');
    const loadingIndicator = this.querySelector('#loadingIndicator');
    const generatingIndicator = this.querySelector('#generatingIndicator');
    const errorMessage = this.querySelector('#errorMessage');

    urlStatus.style.display = 'block';
    loadingIndicator.style.display = 'flex';
    generatingIndicator.style.display = 'none';
    errorMessage.style.display = 'none';
  }

  showGenerating() {
    const urlStatus = this.querySelector('#urlStatus');
    const loadingIndicator = this.querySelector('#loadingIndicator');
    const generatingIndicator = this.querySelector('#generatingIndicator');
    const errorMessage = this.querySelector('#errorMessage');

    urlStatus.style.display = 'block';
    loadingIndicator.style.display = 'none';
    generatingIndicator.style.display = 'flex';
    errorMessage.style.display = 'none';
  }

  showError(message) {
    const urlStatus = this.querySelector('#urlStatus');
    const loadingIndicator = this.querySelector('#loadingIndicator');
    const errorMessage = this.querySelector('#errorMessage');

    urlStatus.style.display = 'block';
    loadingIndicator.style.display = 'none';
    errorMessage.style.display = 'block';
    errorMessage.textContent = message;
  }

  hideStatus() {
    const urlStatus = this.querySelector('#urlStatus');
    urlStatus.style.display = 'none';
  }

  showPreview(metadata) {
    const musicPreview = this.querySelector('#musicPreview');

    const siteIcon = this.getSiteIcon(metadata.siteName, metadata.type);

    musicPreview.innerHTML = `
      <div class="preview-content">
        ${metadata.image ? `
          <img src="${escapeHtml(metadata.image)}" alt="Preview" class="preview-image" />
        ` : `
          <div class="preview-image" style="display: flex; align-items: center; justify-content: center; color: var(--text-muted);">
            ${siteIcon}
          </div>
        `}
        <div class="preview-details">
          <h4 class="preview-title">${escapeHtml(metadata.title || 'Untitled')}</h4>
          ${metadata.description ? `
            <p class="preview-description">${escapeHtml(metadata.description)}</p>
          ` : ''}
          <div class="preview-site">
            <span class="site-icon">${siteIcon}</span>
            <span>${escapeHtml(metadata.siteName || 'Unknown Site')}</span>
          </div>
        </div>
      </div>
    `;

    musicPreview.style.display = 'block';
    this.dispatchEvent(new CustomEvent('music-preview-shown', { bubbles: true }));
  }

  hidePreview() {
    const musicPreview = this.querySelector('#musicPreview');
    musicPreview.style.display = 'none';
  }

  getSiteIcon(siteName, type) {
    if (type && type.startsWith('music.')) {
      const platform = type.split('.')[1];
      const musicIcons = {
        'spotify': '🎵',
        'apple-music': '🍎',
        'youtube': '📺',
        'youtube-music': '🎵',
        'soundcloud': '☁️',
        'tidal': '🌊',
        'deezer': '🎶',
        'bandcamp': '🎪'
      };
      return musicIcons[platform] || '🎵';
    }

    return '🔗';
  }

  updateActions() {
    const musicActions = this.querySelector('.music-actions');
    const musicPostText = this.querySelector('.music-post-text');
    musicActions.style.display = this.urlMetadata ? 'flex' : 'none';
    if (musicPostText) {
      musicPostText.style.display = this.urlMetadata ? 'block' : 'none';
    }
  }

  clearUrl() {
    const urlInput = this.querySelector('#musicUrlInput');
    const postTextArea = this.querySelector('#musicPostText');
    urlInput.value = '';
    this.musicUrl = '';
    this.urlMetadata = null;
    this.postText = '';
    if (postTextArea) {
      postTextArea.value = '';
    }

    this.updateClearButtonVisibility();
    this.hideStatus();
    this.hidePreview();
    this.updateActions();

    urlInput.focus();
  }

  clearMusic() {
    this.clearUrl();
  }

  async shareMusic() {
    if (!this.musicUrl || !this.urlMetadata) return;

    try {
      const shareBtn = this.querySelector('#shareMusicBtn');
      shareBtn.disabled = true;
      shareBtn.textContent = 'Sharing...';

      // Create music post
      const postContent = this.postText.trim() || this.urlMetadata.description || `Sharing music: ${this.urlMetadata.title}`;
      const postData = {
        post_type: 'music',
        music_url: this.musicUrl,
        title: `🎵 ${this.urlMetadata.title}`,
        content: postContent
      };
      Object.assign(postData, this.closest('post-writer')?.getPostStylePayload?.() || {});

      const post = await window.API.posts.create(postData);

      // Clear the music selector
      this.clearMusic();

      // Dispatch event to notify other components
      this.dispatchEvent(new CustomEvent('music-shared', {
        detail: { musicPost: post },
        bubbles: true
      }));

      // Show success feedback
      this.showFeedback('Music shared! 🎵', 'success');

    } catch (error) {
      window.logger.error('Error sharing music:', error);
      const message = error.message || 'Failed to share music. Please try again.';
      const stylePicker = this.closest('post-writer')?.querySelector('#postStylePicker');
      if (stylePicker?.getStyle?.() && stylePicker.showError) {
        stylePicker.showError(message);
      }
      this.showFeedback(message, 'error');
    } finally {
      // Re-enable the share button
      const shareBtn = this.querySelector('#shareMusicBtn');
      if (shareBtn) {
        shareBtn.disabled = false;
        shareBtn.textContent = 'Share Music';
      }
    }
  }

  refreshPostsFeed() {
    // Find and refresh the posts feed component
    const postsFeed = document.querySelector('posts-feed');
    if (postsFeed && postsFeed.load) {
      postsFeed.load();
    }

    // Also refresh activity panel if it exists
    const activityPanel = document.querySelector('activity-panel');
    if (activityPanel && activityPanel.loadFriendActivities) {
      activityPanel.loadFriendActivities();
    }
  }

  showFeedback(message, type = 'info') {
    // Create temporary feedback element
    const feedback = document.createElement('div');
    feedback.className = `music-feedback ${type}`;
    feedback.textContent = message;
    feedback.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 0.75rem 1rem;
      border-radius: 6px;
      color: white;
      font-weight: 500;
      z-index: 10000;
      animation: slideIn 0.3s ease;
      background: ${type === 'success' ? 'var(--success-color)' : type === 'error' ? 'var(--error-color)' : 'var(--info-color)'};
    `;

    document.body.appendChild(feedback);

    // Remove after 3 seconds
    setTimeout(() => {
      feedback.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => feedback.remove(), 300);
    }, 3000);
  }


  // Public method to get current music data
  getMusicData() {
    return {
      musicUrl: this.musicUrl,
      urlMetadata: this.urlMetadata
    };
  }

  // Public method to reset the selector
  reset() {
    this.clearMusic();
  }

  // Job status management methods
  startStatusPolling() {
    // Clear any existing interval
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
    }

    // Check status immediately
    this.checkJobStatus();

    // Then check every 2 seconds
    this.statusCheckInterval = setInterval(() => {
      this.checkJobStatus();
    }, 2000);
  }

  stopStatusPolling() {
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
      this.statusCheckInterval = null;
    }
  }

  async checkJobStatus() {
    if (!this.currentJobId) {
      return;
    }

    try {
      const response = await fetch(`/api/url-metadata/status/${this.currentJobId}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 'completed' && result.metadata) {
        // Job completed successfully
        this.stopStatusPolling();
        this.urlMetadata = result.metadata;
        this.hideStatus();
        this.showPreview(result.metadata);
        this.updateActions();
        this.clearJobStatus();
      } else if (result.status === 'completed' && !result.metadata) {
        // Job completed but metadata is missing (shouldn't happen, but handle gracefully)
        this.stopStatusPolling();
        this.showError(result.error || 'Metadata generation completed but result is unavailable. Please try again.');
        this.urlMetadata = null;
        this.updateActions();
        this.clearJobStatus();
      } else if (result.status === 'failed') {
        // Job failed
        this.stopStatusPolling();
        this.showError(result.error || 'Metadata generation failed');
        this.urlMetadata = null;
        this.updateActions();
        this.clearJobStatus();
      }
      // If status is 'processing', 'waiting', etc., we continue polling

    } catch (error) {
      window.logger.error('Error checking job status:', error);
      // Don't show error immediately, let it retry
    }
  }

  clearJobStatus() {
    this.currentJobId = null;
    this.stopStatusPolling();
  }

  // Cleanup when component is disconnected
  disconnectedCallback() {
    this.stopStatusPolling();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}

// Register the component
customElements.define('music-selector', MusicSelector);

export default MusicSelector;
